import React, { useState, useMemo, useEffect } from 'react';
export interface DataGridColumn<T> {
  key: keyof T;
  label: string;
  sortable?: boolean;
  hidden?: boolean; // Hide column from display but keep for search
  render?: (value: any, item: T) => React.ReactNode;
}

export interface DataGridAction<T> {
  label: string;
  onClick: (item: T) => void;
  variant?: 'primary' | 'danger';
}

export interface DataGridProps<T> {
  sortField?: string;
  sortDirection?: "asc" | "desc";
  onSortChange?: (field: string, direction: "asc" | "desc") => void;
  data: T[];
  columns: DataGridColumn<T>[];
  actions?: DataGridAction<T>[];
  searchPlaceholder?: string;
  searchTerm?: string;
  onSearchTermChange?: (term: string) => void;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  noResultsTitle?: string;
  noResultsDescription?: string;
  loading?: boolean;
  error?: string | null;
  pageSize?: number;
  showPagination?: boolean;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}


export function DataGrid<T extends Record<string, any>>({
  sortField = "",
  sortDirection = "asc",
  onSortChange,
  data,
  columns,
  actions = [],
  searchPlaceholder = "Cerca...",
  searchTerm = "",
  onSearchTermChange,
  emptyStateTitle = "Nessun elemento",
  emptyStateDescription = "Non ci sono elementi da visualizzare.",
  noResultsTitle = "Nessun risultato",
  noResultsDescription = "Prova a modificare i termini di ricerca.",
  loading = false,
  error = null,
  pageSize = 10,
  showPagination = true,
  currentPage: controlledPage,
  totalPages: controlledTotalPages,
  onPageChange,
  onPageSizeChange
}: DataGridProps<T>) {
  // La ricerca e l'ordinamento sono ora gestiti dal parent
  // Il sort è ora controllato dal parent
  const [internalPage, setInternalPage] = useState(1);
  const isControlled = typeof controlledPage === 'number' && typeof controlledTotalPages === 'number' && typeof onPageChange === 'function';
  const currentPage = isControlled ? controlledPage : internalPage;

  const sortableColumns = columns.filter(col => col.sortable !== false && !col.hidden);
  const visibleColumns = columns.filter(col => !col.hidden);

  const filteredAndSortedData = useMemo(() => {
    // La ricerca e l'ordinamento sono ora gestiti dal parent (server-side)
    let filtered = data;
    // Ordina localmente solo se serve
    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        const aValue = a[sortField] || '';
        const bValue = b[sortField] || '';
        const comparison = String(aValue).localeCompare(String(bValue));
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }
    return filtered;
  }, [data, sortField, sortDirection]);

  // Pagination logic
  const totalPages = isControlled ? controlledTotalPages! : Math.ceil(filteredAndSortedData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = showPagination
    ? (isControlled ? data : filteredAndSortedData.slice(startIndex, endIndex))
    : filteredAndSortedData;

  // Reset to first page when search or data changes
  useEffect(() => {
    if (!isControlled) setInternalPage(1);
  }, [searchTerm, data, isControlled]);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Errore</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search and Filter Bar */}
      <div className="management-filters flex flex-col sm:flex-row gap-4">
        {/* Search Input */}
        <div className="flex-1">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              aria-label={searchPlaceholder}
              placeholder={searchPlaceholder}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
              value={searchTerm}
              onChange={(e) => onSearchTermChange && onSearchTermChange(e.target.value)}
            />
          </div>
        </div>

        {/* Sort Dropdown */}
        {sortableColumns.length > 0 && (
          <div className="sm:w-48">
            <select
              aria-label="Ordina risultati"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-brand-500 focus:border-brand-500"
              value={sortField ? `${String(sortField)}-${sortDirection}` : ''}
              onChange={(e) => {
                if (e.target.value && onSortChange) {
                  const [field, direction] = e.target.value.split('-');
                  onSortChange(field, direction as "asc" | "desc");
                } else if (onSortChange) {
                  onSortChange("", "asc");
                }
              }}
            >
              <option value="">Ordina per...</option>
              {sortableColumns.map((column) => (
                <React.Fragment key={String(column.key)}>
                  <option value={`${String(column.key)}-asc`}>{column.label} A-Z</option>
                  <option value={`${String(column.key)}-desc`}>{column.label} Z-A</option>
                </React.Fragment>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Results Count and Pagination Info */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
        <div className="text-sm text-gray-600">
          {showPagination ? (
            <>
              Mostra {startIndex + 1}-{Math.min(endIndex, filteredAndSortedData.length)} di {filteredAndSortedData.length} elementi
              {filteredAndSortedData.length !== data.length && (
                <span className="text-gray-500"> (filtrati da {data.length} totali)</span>
              )}
            </>
          ) : (
            <>
              {filteredAndSortedData.length} di {data.length} elementi
            </>
          )}
        </div>
        
        {showPagination && totalPages > 1 && (
          <div className="text-sm text-gray-600">
            Pagina {currentPage} di {totalPages}
          </div>
        )}
      </div>

      {/* Data Grid */}
      <div className="grid grid-cols-1 gap-6">
        {paginatedData.map((item, index) => (
          <div key={index} className="management-record p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0 space-y-2">
                {visibleColumns.map((column) => {
                  const value = item[column.key];
                  const displayValue = column.render ? column.render(value, item) : value;
                  
                  return (
                    <div key={String(column.key)}>
                      {column.key === visibleColumns[0].key ? (
                        // First visible column as title
                        <h3 className="text-lg font-semibold text-gray-900 break-words">
                          {displayValue}
                        </h3>
                      ) : (
                        // Other columns as regular content
                        displayValue && (
                          <p className="text-sm text-gray-500 break-words">
                            <span className="font-medium">{column.label}:</span> {displayValue}
                          </p>
                        )
                      )}
                    </div>
                  );
                })}
                
                {/* Actions */}
                {actions.length > 0 && (
                  <div className="management-record__actions mt-4 flex flex-wrap gap-3">
                    {actions.map((action, actionIndex) => (
                      <button
                        key={actionIndex}
                        onClick={() => action.onClick(item)}
                        className={`text-sm font-medium ${
                          action.variant === 'danger'
                            ? 'text-red-600 hover:text-red-800'
                            : 'text-brand-600 hover:text-brand-800'
                        }`}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {paginatedData.length === 0 && filteredAndSortedData.length > 0 && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">{noResultsTitle}</h3>
          <p className="mt-1 text-sm text-gray-500">{noResultsDescription}</p>
        </div>
      )}

      {/* No Data State */}
      {filteredAndSortedData.length === 0 && data.length > 0 && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">{noResultsTitle}</h3>
          <p className="mt-1 text-sm text-gray-500">{noResultsDescription}</p>
        </div>
      )}

      {/* No Data State */}
      {data.length === 0 && !loading && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">{emptyStateTitle}</h3>
          <p className="mt-1 text-sm text-gray-500">{emptyStateDescription}</p>
        </div>
      )}

      {/* Pagination Controls */}
      {showPagination && totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <React.Fragment>
              <button
                onClick={() => isControlled ? onPageChange && onPageChange(currentPage - 1) : setInternalPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Precedente
              </button>
              <button
                onClick={() => isControlled ? onPageChange && onPageChange(currentPage + 1) : setInternalPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Successiva
              </button>
            </React.Fragment>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Mostra <span className="font-medium">{startIndex + 1}</span> - <span className="font-medium">{Math.min(endIndex, filteredAndSortedData.length)}</span> di{' '}
                <span className="font-medium">{filteredAndSortedData.length}</span> risultati
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md " aria-label="Pagination">
                <button
                  onClick={() => isControlled ? onPageChange && onPageChange(currentPage - 1) : setInternalPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Precedente</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                  </svg>
                </button>
                {/* Page Numbers */}
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                  // Show first page, last page, current page, and pages around current
                  const showPage = pageNum === 1 || 
                                   pageNum === totalPages || 
                                   Math.abs(pageNum - currentPage) <= 1;
                  if (!showPage) {
                    // Show dots for gaps
                    if (pageNum === 2 && currentPage > 4) {
                      return (
                        <span key={pageNum} className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300">
                          ...
                        </span>
                      );
                    }
                    if (pageNum === totalPages - 1 && currentPage < totalPages - 3) {
                      return (
                        <span key={pageNum} className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300">
                          ...
                        </span>
                      );
                    }
                    return null;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => isControlled ? onPageChange && onPageChange(pageNum) : setInternalPage(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                        pageNum === currentPage
                          ? 'z-10 bg-brand-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600'
                          : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => isControlled ? onPageChange && onPageChange(currentPage + 1) : setInternalPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Successiva</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
